import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Lock, ShieldCheck, ShieldX, Copy, CheckCircle2, AlertTriangle, Eye, MousePointerClick, TrendingUp, ExternalLink, MessageCircle, PlayCircle } from 'lucide-react';
import { API_URL } from '../utils/apiUrl';

const CTR_LEVELS = [
  { max: 3, label: 'Zayıf', color: '#991b1b' },
  { max: 7, label: 'Ortalama', color: '#d97706' },
  { max: 12, label: 'İyi', color: '#15803d' },
  { max: 18, label: 'Çok İyi', color: '#166534' },
  { max: Infinity, label: 'Mükemmel', color: '#064e3b' },
];
const getCTRLevel = (ctr) => CTR_LEVELS.find(l => ctr < l.max) || CTR_LEVELS[CTR_LEVELS.length - 1];

const fmtCTR = (val) => {
  const n = val == null || isNaN(val) || val < 0 ? 0 : val;
  return `%${n.toFixed(2).replace('.', ',')}`;
};

const safe = (v) => (v == null || isNaN(v) || v < 0 ? 0 : v);

const SellerReportPage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState('all');
  const [copied, setCopied] = useState(null);

  const fetchReport = async (r) => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API_URL}/api/seller-report/${token}?range=${r}`);
      setData(d);
      setError(null);
    } catch (e) {
      setError(e.response?.status === 404 ? 'Bu rapor linki geçersizdir.' : 'Rapor yüklenemedi.');
    }
    setLoading(false);
  };

  useEffect(() => {
    let m = document.querySelector('meta[name="robots"]');
    if (!m) { m = document.createElement('meta'); m.name = 'robots'; document.head.appendChild(m); }
    m.content = 'noindex, nofollow';
    fetchReport(range);
  }, [token, range]);

  const handleCopy = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const buildWaLink = (product) => {
    if (!data) return '#';
    const phone = data.whatsapp_number;
    const msg = `Merhaba, TestettiK üzerinden ürünüm için doğrulama paketini aktif etmek istiyorum.\nÜrün: ${product.name}\nSatıcı: ${data.seller.name}`;
    return `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
  };

  if (loading && !data) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-section)' }}>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Yükleniyor...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-section)' }}>
        <div className="text-center">
          <AlertTriangle className="w-10 h-10 mx-auto mb-3" style={{ color: '#d97706' }} />
          <p className="text-base font-bold mb-2" style={{ color: 'var(--text-primary)' }}>{error}</p>
          <Link to="/" className="text-sm font-semibold" style={{ color: 'var(--green-main)' }}>Ana Sayfaya Dön</Link>
        </div>
      </div>
    );
  }

  const { seller, summary, products } = data;
  const hasAnyActive = summary.active_badges > 0;
  const activeProducts = products.filter(p => p.report_state === 'active');
  const bestCTR = activeProducts.length > 0 ? activeProducts.reduce((a, b) => safe(a.ctr) > safe(b.ctr) ? a : b) : null;
  const worstCTR = activeProducts.length > 1 ? activeProducts.reduce((a, b) => safe(a.ctr) < safe(b.ctr) ? a : b) : null;
  const rangeLabel = range === '7d' ? 'Son 7 Gün' : range === '30d' ? 'Son 30 Gün' : 'Tüm Zamanlar';

  return (
    <div className="min-h-screen overflow-x-hidden" style={{ background: 'var(--bg-section)' }} data-testid="seller-report">
      {/* Header */}
      <header className="border-b py-4 px-5" style={{ background: 'var(--bg-main)', borderColor: 'var(--border-soft)' }}>
        <div className="max-w-[1200px] mx-auto flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold tracking-wide uppercase" style={{ color: 'var(--text-secondary)' }}>TestettiK® Satıcı Raporu</p>
            <p className="text-lg font-black" style={{ color: 'var(--text-primary)' }} data-testid="seller-name">{seller.name}</p>
          </div>
          <div className="flex gap-2">
            {['7d', '30d', 'all'].map(r => (
              <button key={r} onClick={() => setRange(r)}
                className={`px-3 py-1.5 text-xs font-bold rounded transition ${range === r ? 'text-white' : ''}`}
                style={range === r ? { background: 'var(--green-main)' } : { background: 'var(--bg-soft)', color: 'var(--text-secondary)' }}
                data-testid={`range-${r}`}>
                {r === '7d' ? '7 Gün' : r === '30d' ? '30 Gün' : 'Tümü'}
              </button>
            ))}
          </div>
        </div>
      </header>

      <div className="max-w-[1200px] mx-auto px-5 py-8">
        <p className="text-xs mb-4" style={{ color: 'var(--text-secondary)' }}>Dönem: <strong>{rangeLabel}</strong></p>

        {/* Summary KPIs — 4 columns */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="summary-kpis">
          <KPI label="Toplam Görüntülenme" value={hasAnyActive ? safe(summary.total_views) : null} icon={<Eye className="w-4 h-4" />} />
          <KPI label="Ürün Tıklaması" value={hasAnyActive ? safe(summary.total_product_clicks) : null} icon={<MousePointerClick className="w-4 h-4" />} />
          <KPI label="Satıcıya Yönlendirme" value={hasAnyActive ? safe(summary.total_seller_clicks) : null} icon={<ExternalLink className="w-4 h-4" />} />
          <KPI label="CTR" value={hasAnyActive ? fmtCTR(summary.total_views > 0 ? (safe(summary.total_seller_clicks) / safe(summary.total_views) * 100) : 0) : null} icon={<TrendingUp className="w-4 h-4" />} isText />
        </div>

        {/* FOMO banner */}
        {!hasAnyActive && (
          <div className="rounded-lg p-5 border mb-6" style={{ background: '#FFFBEB', borderColor: '#FCD34D' }} data-testid="fomo-banner">
            <p className="text-sm font-bold mb-1" style={{ color: '#92400E' }}>Performans verilerinizin tamamı şu anda kilitlidir.</p>
            <p className="text-xs" style={{ color: '#A16207' }}>Ürünleriniz kullanıcılar tarafından incelenmektedir ancak kaç kişinin satın alma sayfanıza geçtiğini göremiyorsunuz.</p>
          </div>
        )}

        {/* Product Cards */}
        <div className="space-y-6" data-testid="product-list">
          {products.map(product => (
            <ProductCard key={product.id} product={product} data={data} buildWaLink={buildWaLink} handleCopy={handleCopy} copied={copied} />
          ))}
        </div>

        {products.length === 0 && (
          <div className="text-center py-16">
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Bu satıcıya ait ürün bulunmuyor.</p>
          </div>
        )}

        {/* CTR Panel — ALWAYS OPEN */}
        <div className="mt-8 border rounded-lg overflow-hidden" style={{ borderColor: 'var(--border-soft)' }} data-testid="ctr-panel">
          <div className="p-4" style={{ background: 'var(--bg-soft)' }}>
            <span className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>CTR Nedir? Karşılaştırma Tablosu</span>
          </div>
          <div className="p-5 space-y-5" style={{ background: 'var(--bg-main)' }}>
            <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>CTR (Click-Through Rate), doğrulama sayfanızı görüntüleyen kişilerin yüzde kaçının satıcı sayfanıza yönlendirildiğini gösterir.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-bold mb-2" style={{ color: 'var(--text-primary)' }}>Türkiye E-Ticaret Ortalaması</p>
                <div className="space-y-1.5">
                  {[['%0–3', 'Zayıf', '#991b1b'], ['%3–7', 'Ortalama', '#d97706'], ['%7–12', 'İyi', '#15803d'], ['%12–18', 'Çok İyi', '#166534'], ['%18+', 'Mükemmel', '#064e3b']].map(([r, l, c]) => (
                    <div key={r} className="flex items-center justify-between text-xs py-1.5 px-2.5 rounded" style={{ background: 'var(--bg-section)' }}>
                      <span style={{ color: 'var(--text-secondary)' }}>{r}</span>
                      <span className="font-bold" style={{ color: c }}>{l}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-bold mb-2" style={{ color: 'var(--text-primary)' }}>Global Ortalama</p>
                <div className="space-y-1.5">
                  {[['%0–2', 'Düşük', '#991b1b'], ['%2–5', 'Orta', '#d97706'], ['%5–10', 'İyi', '#15803d'], ['%10–15', 'Çok İyi', '#166534'], ['%15+', 'Mükemmel', '#064e3b']].map(([r, l, c]) => (
                    <div key={r} className="flex items-center justify-between text-xs py-1.5 px-2.5 rounded" style={{ background: 'var(--bg-section)' }}>
                      <span style={{ color: 'var(--text-secondary)' }}>{r}</span>
                      <span className="font-bold" style={{ color: c }}>{l}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Recommendations — Enhanced */}
            <div>
              <p className="text-xs font-bold mb-3" style={{ color: 'var(--text-primary)' }}>Performans Artırma Önerileri</p>
              {hasAnyActive ? (
                <ul className="space-y-2 text-xs" style={{ color: 'var(--text-secondary)' }}>
                  <li className="flex items-start gap-2 p-2 rounded" style={{ background: '#ECFDF5' }}>
                    <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: '#065F46' }} />
                    <span><strong style={{ color: '#065F46' }}>Doğrulama linkini ürün açıklamanızda görünür şekilde kullanın.</strong> <span className="inline-block px-1.5 py-0.5 rounded text-[9px] font-bold text-white" style={{ background: '#065F46' }}>EN KRİTİK</span></span>
                  </li>
                  <li className="flex items-start gap-2 p-2 rounded" style={{ background: '#ECFDF5' }}>
                    <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: '#065F46' }} />
                    <span><strong style={{ color: '#065F46' }}>Ürün reklamlarınızda doğrulama sayfasına yönlendirme yapın.</strong> <span className="inline-block px-1.5 py-0.5 rounded text-[9px] font-bold text-white" style={{ background: '#065F46' }}>EN KRİTİK</span></span>
                  </li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: 'var(--green-main)' }} />Instagram biyografinize doğrulama linkinizi ekleyin.</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: 'var(--green-main)' }} />Ürün sayfanızda TestettiK® Güven Rozeti'nden bahsedin.</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: 'var(--green-main)' }} />Sosyal medya hikayelerinde doğrulama sonucunu paylaşın.</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: 'var(--green-main)' }} />Müşteri itirazlarında doğrulama linkini kullanın.</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: 'var(--green-main)' }} />WhatsApp satış görüşmelerinde link paylaşın.</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: 'var(--green-main)' }} />Ürününüz 7.0+ ise bunu net belirtin.</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: 'var(--green-main)' }} />Premium görünürlük aldıysanız aktif kullanın.</li>
                  <li className="flex items-start gap-2"><AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: '#d97706' }} /><span style={{ color: '#92400E' }}>Link görünür değilse dönüşüm düşer.</span></li>
                </ul>
              ) : (
                <ul className="space-y-1.5 text-xs" style={{ color: 'var(--text-secondary)' }}>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: 'var(--green-main)' }} />Doğrulama linkini ürün sayfanızda kullanın</li>
                  <li className="flex items-start gap-2"><Lock className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: 'var(--text-secondary)' }} />Daha fazla öneri için rozeti aktif edin</li>
                </ul>
              )}
            </div>

            {bestCTR && worstCTR && bestCTR.id !== worstCTR.id && (
              <div className="grid grid-cols-2 gap-3 pt-2">
                <div className="p-3 rounded border" style={{ borderColor: 'var(--border-soft)', background: 'var(--bg-section)' }}>
                  <p className="text-[10px] font-bold uppercase" style={{ color: '#15803d' }}>En Yüksek CTR</p>
                  <p className="text-xs font-bold mt-1" style={{ color: 'var(--text-primary)' }}>{bestCTR.name}</p>
                  <p className="text-lg font-black" style={{ color: '#15803d' }}>{fmtCTR(bestCTR.ctr)}</p>
                </div>
                <div className="p-3 rounded border" style={{ borderColor: 'var(--border-soft)', background: 'var(--bg-section)' }}>
                  <p className="text-[10px] font-bold uppercase" style={{ color: '#991b1b' }}>En Düşük CTR</p>
                  <p className="text-xs font-bold mt-1" style={{ color: 'var(--text-primary)' }}>{worstCTR.name}</p>
                  <p className="text-lg font-black" style={{ color: '#991b1b' }}>{fmtCTR(worstCTR.ctr)}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* TT-ROZET-001-FINAL — Ürün Test Sonuçları (always visible, at bottom) */}
        <section className="mt-10 pt-8 border-t" style={{ borderColor: 'var(--border-soft)' }} data-testid="seller-test-results-section">
          <div className="mb-6">
            <h2 className="text-lg md:text-xl font-black tracking-tight" style={{ color: 'var(--text-primary)' }}>
              Ürün Test Sonuçları
            </h2>
            <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
              TestettiK® bağımsız değerlendirme sonuçlarınız
            </p>
          </div>
          <div className="space-y-5" data-testid="seller-test-results-list">
            {products.map(product => (
              <SellerTestResultCard
                key={`tr-${product.id}`}
                product={product}
                waNumber={data?.whatsapp_number}
              />
            ))}
            {products.length === 0 && (
              <p className="text-sm text-center py-8" style={{ color: 'var(--text-secondary)' }}>
                Bu satıcıya ait test edilmiş ürün bulunmuyor.
              </p>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

/* ── KPI Card ── */
const KPI = ({ label, value, icon, isText }) => (
  <div className="rounded-lg p-4 border" style={{ background: 'var(--bg-main)', borderColor: 'var(--border-soft)' }}>
    <div className="flex items-center gap-1.5 mb-2" style={{ color: 'var(--text-secondary)' }}>{icon}<span className="text-[10px] font-bold uppercase tracking-wide">{label}</span></div>
    {value !== null ? (
      <p className="text-2xl font-black" style={{ color: 'var(--text-primary)' }}>{isText ? value : value}</p>
    ) : (
      <div className="flex items-center gap-1"><Lock className="w-4 h-4" style={{ color: 'var(--text-secondary)' }} /><span className="text-sm font-bold" style={{ color: 'var(--text-secondary)' }}>Kilitli</span></div>
    )}
  </div>
);

/* ── Product Report Card ── */
const ProductCard = ({ product, data, buildWaLink, handleCopy, copied }) => {
  const state = product.report_state;
  const isExpired = state === 'locked' && product.score >= 7 && product.badge_expires_at;

  const Header = () => (
    <div className="flex items-start justify-between mb-4">
      <div>
        <h3 className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>{product.name}</h3>
        <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{product.brand}</p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <div className="px-3 py-1.5 rounded text-white text-xs font-bold" style={{ background: product.score >= 7 ? 'var(--green-main)' : '#991b1b' }}>
          {product.score?.toFixed(1)}/10
        </div>
        {state === 'active' && (
          <div className="flex items-center gap-1 px-2 py-1.5 rounded text-xs font-bold" style={{ background: '#ECFDF5', color: '#065F46' }}>
            <CheckCircle2 className="w-3 h-3" /> Aktif
          </div>
        )}
      </div>
    </div>
  );

  if (state === 'failed') {
    return (
      <div className="rounded-lg border overflow-hidden" style={{ background: 'var(--bg-main)', borderColor: 'var(--border-soft)' }} data-testid={`report-card-${product.id}`}>
        <div className="p-5">
          <Header />
          <div className="flex items-center gap-2 py-2.5 px-3 rounded" style={{ background: '#FEF2F2' }}>
            <ShieldX className="w-4 h-4" style={{ color: '#991b1b' }} />
            <span className="text-xs font-semibold" style={{ color: '#991b1b' }}>TESTİ GEÇEMEDİ</span>
          </div>
          <p className="text-xs mt-3" style={{ color: 'var(--text-secondary)' }}>Bu ürün test kriterlerini karşılamadığı için performans raporu oluşturulmaz.</p>
        </div>
      </div>
    );
  }

  if (state === 'locked') {
    return (
      <div className="rounded-lg border overflow-hidden" style={{ background: 'var(--bg-main)', borderColor: 'var(--border-soft)' }} data-testid={`report-card-${product.id}`}>
        <div className="p-5">
          <Header />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
            {['Görüntülenme', 'Ürün Tıklaması', 'Yönlendirme', 'CTR'].map(label => (
              <div key={label} className="p-3 rounded border text-center" style={{ background: 'var(--bg-section)', borderColor: 'var(--border-soft)' }}>
                <Lock className="w-4 h-4 mx-auto mb-1" style={{ color: 'var(--text-secondary)' }} />
                <p className="text-[9px] font-bold uppercase leading-tight" style={{ color: 'var(--text-secondary)' }}>{label}</p>
              </div>
            ))}
          </div>
          <div className="rounded p-3 mb-4" style={{ background: '#FFFBEB' }}>
            {isExpired ? (
              <>
                <p className="text-xs font-bold mb-1" style={{ color: '#92400E' }}>Bu ürün artık doğrulama linki ile satış avantajı sağlayamaz.</p>
                <p className="text-[11px]" style={{ color: '#A16207' }}>Doğrulama aktif olmadığı için dönüşüm etkisi düşmektedir.</p>
              </>
            ) : (
              <>
                <p className="text-xs font-bold mb-1" style={{ color: '#92400E' }}>Bu ürün için performans verileri oluşmaktadır.</p>
                <p className="text-[11px]" style={{ color: '#A16207' }}>Her geçen gün veri birikiyor ancak erişiminiz kapalı.</p>
              </>
            )}
          </div>
          <a href={buildWaLink(product)} target="_blank" rel="noopener noreferrer"
            className="block w-full text-center py-3 rounded-lg text-white text-sm font-bold transition hover:opacity-90"
            style={{ background: 'var(--green-main)' }} data-testid={`activate-cta-${product.id}`}>
            {isExpired ? 'Rozeti Yenile' : 'Doğrulamayı Aktif Et'}
          </a>
        </div>
      </div>
    );
  }

  // ACTIVE
  const views = safe(product.views);
  const pClicks = safe(product.product_clicks);
  const sClicks = safe(product.seller_clicks);
  const ctr = safe(product.ctr);
  const ctrLevel = getCTRLevel(ctr);

  return (
    <div className="rounded-lg border overflow-hidden" style={{ background: 'var(--bg-main)', borderColor: 'var(--border-soft)' }} data-testid={`report-card-${product.id}`}>
      <div className="p-5">
        <Header />

        {/* Verification link */}
        {product.verify_slug && (
          <div className="rounded p-3 mb-4 border" style={{ background: 'var(--bg-accent-soft)', borderColor: 'var(--border-soft)' }}>
            <div className="flex items-center justify-between mb-1">
              <p className="text-[10px] font-bold uppercase" style={{ color: 'var(--text-secondary)' }}>Doğrulama Linki</p>
              <button onClick={() => handleCopy(`https://www.testettik.com/v/${product.verify_slug}`, `slug-${product.id}`)}
                className="flex items-center gap-1 text-[11px] font-bold" style={{ color: 'var(--green-main)' }}
                data-testid={`copy-link-${product.id}`}>
                {copied === `slug-${product.id}` ? <><CheckCircle2 className="w-3 h-3" /> Kopyalandı</> : <><Copy className="w-3 h-3" /> Kopyala</>}
              </button>
            </div>
            <p className="text-xs font-mono break-all" style={{ color: 'var(--text-primary)' }}>https://www.testettik.com/v/{product.verify_slug}</p>
            <p className="text-[10px] mt-2" style={{ color: 'var(--text-secondary)' }}>Bu link satış sayfanızda aktif kullanılmalıdır. Kullanılmadığında dönüşüm düşer.</p>
          </div>
        )}

        {/* 4-column metrics — ALWAYS VISIBLE */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4" data-testid={`metrics-${product.id}`}>
          <div className="p-3 rounded border text-center" style={{ background: 'var(--bg-section)', borderColor: 'var(--border-soft)' }}>
            <p className="text-xl font-black" style={{ color: 'var(--text-primary)' }} data-testid={`views-${product.id}`}>{views}</p>
            <p className="text-[9px] font-bold uppercase leading-tight" style={{ color: 'var(--text-secondary)' }}>Görüntülenme</p>
          </div>
          <div className="p-3 rounded border text-center" style={{ background: 'var(--bg-section)', borderColor: 'var(--border-soft)' }}>
            <p className="text-xl font-black" style={{ color: 'var(--text-primary)' }} data-testid={`pclicks-${product.id}`}>{pClicks}</p>
            <p className="text-[9px] font-bold uppercase leading-tight" style={{ color: 'var(--text-secondary)' }}>Ürün Tıklaması</p>
          </div>
          <div className="p-3 rounded border text-center" style={{ background: 'var(--bg-section)', borderColor: 'var(--border-soft)' }}>
            <p className="text-xl font-black" style={{ color: 'var(--text-primary)' }} data-testid={`sclicks-${product.id}`}>{sClicks}</p>
            <p className="text-[9px] font-bold uppercase leading-tight" style={{ color: 'var(--text-secondary)' }}>Yönlendirme</p>
          </div>
          <div className="p-3 rounded border text-center" style={{ background: 'var(--bg-section)', borderColor: 'var(--border-soft)' }}>
            <p className="text-xl font-black" style={{ color: ctrLevel.color }} data-testid={`ctr-${product.id}`}>{fmtCTR(ctr)}</p>
            <p className="text-[9px] font-bold uppercase leading-tight" style={{ color: ctrLevel.color }}>{ctrLevel.label}</p>
          </div>
        </div>

        {/* Insight row */}
        <div className="flex items-center gap-3 mb-4 text-xs flex-wrap" style={{ color: 'var(--text-secondary)' }}>
          <span><strong style={{ color: 'var(--text-primary)' }}>{views}</strong> kişi görüntüledi</span>
          <span className="w-px h-3 inline-block" style={{ background: 'var(--border-soft)' }} />
          <span><strong style={{ color: 'var(--text-primary)' }}>{sClicks}</strong> kişi satıcıya geçti</span>
        </div>

        {/* Expiry + update time */}
        <div className="flex items-center gap-4 mb-3 flex-wrap">
          {product.badge_expires_at && (
            <p className="text-[11px]" style={{ color: 'var(--text-secondary)' }}>Rozet geçerlilik: <strong>{product.badge_expires_at.split('T')[0]}</strong></p>
          )}
          {product.data_updated_at && (
            <p className="text-[11px]" style={{ color: 'var(--text-secondary)' }}>Son güncelleme: {new Date(product.data_updated_at).toLocaleString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</p>
          )}
        </div>

        {/* Source breakdown */}
        {product.source_breakdown && Object.keys(product.source_breakdown).length > 0 && (
          <div className="rounded p-3 border" style={{ background: 'var(--bg-section)', borderColor: 'var(--border-soft)' }}>
            <p className="text-[10px] font-bold uppercase mb-2" style={{ color: 'var(--text-secondary)' }}>Trafik Kaynakları</p>
            <div className="space-y-1">
              {Object.entries(product.source_breakdown).map(([src, count]) => (
                <div key={src} className="flex justify-between text-xs">
                  <span style={{ color: 'var(--text-secondary)' }}>{src}</span>
                  <span className="font-bold" style={{ color: 'var(--text-primary)' }}>{count}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SellerReportPage;

/* ── TT-ROZET-001-FINAL: Seller test result card (bottom section) ── */
const SellerTestResultCard = ({ product, waNumber }) => {
  const scoreNum = Number(product.score) || 0;
  const scorePassed = scoreNum >= 7;
  const badgeValid = !!product.badge_valid;
  const verdictText = product.verdict_display
    || (scorePassed ? 'TEST BAŞARILI' : 'BEKLENTİYİ KARŞILAMADI');
  const verdictUpper = String(verdictText).toUpperCase();
  const isSuccess = verdictUpper.includes('BAŞARILI');
  const testDateStr = (() => {
    const raw = product.test_date;
    if (!raw) return null;
    try {
      const d = new Date(raw);
      if (isNaN(d.getTime())) return raw;
      return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
    } catch { return raw; }
  })();
  const expiryStr = (() => {
    if (!product.badge_expires_at) return null;
    try {
      const d = new Date(product.badge_expires_at);
      if (isNaN(d.getTime())) return null;
      return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
    } catch { return null; }
  })();

  const verifyUrl = product.verify_slug ? `testettik.com/v/${product.verify_slug}` : null;
  const waHref = waNumber ? `https://wa.me/${String(waNumber).replace(/\D/g, '')}` : null;

  return (
    <div
      className="border rounded-xl bg-white p-5"
      style={{ borderColor: 'var(--border-soft)' }}
      data-testid={`seller-test-result-${product.id}`}
    >
      {/* Header: name + score + result label */}
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div className="min-w-0">
          <h3 className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>{product.name}</h3>
          <p className="text-xs mt-0.5" style={{ color: 'var(--text-secondary)' }}>{product.brand || '—'}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <div
            className="px-3 py-1.5 rounded-lg text-white text-sm font-black tracking-tight"
            style={{ background: scorePassed ? 'var(--green-main)' : '#991b1b' }}
            data-testid={`test-result-score-${product.id}`}
          >
            {scoreNum.toFixed(1)} / 10
          </div>
          <span
            className={`px-2.5 py-1 rounded text-[11px] font-black uppercase tracking-wider ${
              isSuccess ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'
            }`}
            data-testid={`test-result-verdict-${product.id}`}
          >
            {verdictUpper}
          </span>
        </div>
      </div>

      {/* Meta: date + category */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs mb-3" style={{ color: 'var(--text-secondary)' }}>
        {testDateStr && <span>Test Tarihi: <span className="font-semibold text-neutral-800">{testDateStr}</span></span>}
        {product.category && <span>Kategori: <span className="font-semibold text-neutral-800">{product.category}</span></span>}
      </div>

      {/* Criteria */}
      {(product.test_criteria || []).length > 0 && (
        <div className="mb-4">
          <p className="text-[11px] font-black uppercase tracking-widest mb-1.5" style={{ color: 'var(--text-secondary)' }}>
            Test Kriterleri
          </p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5">
            {(product.test_criteria || []).slice(0, 12).map((c, i) => (
              <li key={i} className="flex items-start gap-1.5 text-xs" style={{ color: 'var(--text-primary)' }}>
                <CheckCircle2 className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: 'var(--green-main)' }} />
                <span>{c}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Video */}
      {(product.video?.playback_url || product.video_url) && (
        <div className="flex items-center gap-2 text-xs mb-3" style={{ color: 'var(--green-main)' }}>
          <PlayCircle className="w-4 h-4" />
          <span className="font-semibold">Test videosu doğrulama sayfasında yayında</span>
        </div>
      )}

      {/* Badge status line */}
      <div
        className={`flex items-center gap-2 px-3 py-2 rounded text-xs font-bold ${
          badgeValid ? 'bg-emerald-50 text-emerald-900' : 'bg-red-50 text-red-900'
        }`}
        data-testid={`test-result-badge-status-${product.id}`}
      >
        {badgeValid ? <ShieldCheck className="w-3.5 h-3.5" /> : <ShieldX className="w-3.5 h-3.5" />}
        {badgeValid
          ? <>✓ Güven Rozeti Aktif{expiryStr ? ` — ${expiryStr}` : ''}</>
          : <>✗ Güven Rozeti Aktif Değil</>}
      </div>

      {/* Verification URL */}
      {verifyUrl && (
        <div className="mt-2 text-xs" data-testid={`test-result-verify-url-${product.id}`}>
          <span style={{ color: 'var(--text-secondary)' }}>Doğrulama Linki: </span>
          {badgeValid ? (
            <a
              href={`https://${verifyUrl}`}
              target="_blank"
              rel="noopener noreferrer"
              className="font-mono font-bold hover:underline"
              style={{ color: 'var(--green-main)' }}
            >
              {verifyUrl}
            </a>
          ) : (
            <span className="font-mono font-bold" style={{ color: 'var(--text-secondary)' }}>
              {verifyUrl} <span className="ml-1 not-italic font-semibold" style={{ color: '#991b1b' }}>(Güven Rozeti gerekli)</span>
            </span>
          )}
        </div>
      )}

      {/* CTA / status box */}
      {badgeValid ? (
        <div
          className="mt-4 p-3 rounded border-l-4 border-emerald-600 bg-emerald-50 text-xs"
          data-testid={`test-result-cta-active-${product.id}`}
        >
          <p className="font-bold text-emerald-900">✓ Güven Rozeti Aktif — Doğrulama linkiniz çalışıyor. Müşterileriniz test sonuçlarınızı görebilir.</p>
        </div>
      ) : (
        <div
          className="mt-4 p-4 rounded-lg border-l-4 border-emerald-800 bg-emerald-50/60"
          data-testid={`test-result-cta-inactive-${product.id}`}
        >
          <p className="text-sm font-black mb-2" style={{ color: '#1C3B2A' }}>Bu sonuçları müşterilerinizle paylaşın</p>
          <p className="text-xs leading-relaxed mb-3 whitespace-pre-line" style={{ color: '#065F46' }}>
{`TestettiK® Güven Rozeti aktif olduğunda:
✓ Doğrulama linkiniz aktif hale gelir
✓ Müşterileriniz test sonuçlarını görebilir
✓ CTR ve performans verileriniz izlenebilir hale gelir

Güven Rozeti edinmek için WhatsApp üzerinden bizimle iletişime geçin.`}
          </p>
          {waHref ? (
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-xs font-bold hover:opacity-90"
              style={{ background: '#1C3B2A' }}
              data-testid={`test-result-cta-wa-${product.id}`}
            >
              <MessageCircle className="w-3.5 h-3.5" />
              WhatsApp ile İletişime Geç
            </a>
          ) : (
            <span
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-neutral-200 text-neutral-500 text-xs font-bold cursor-not-allowed"
              data-testid={`test-result-cta-wa-disabled-${product.id}`}
            >
              <MessageCircle className="w-3.5 h-3.5" />
              İletişim bilgisi yapılandırılmamış
            </span>
          )}
        </div>
      )}
    </div>
  );
};
